import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getMyCourses, joinCourse, courseErrorMessage } from "../../courses";

const LANGUAGE_LABEL_KEYS = {
    "de-DE": "German",
    "en-GB": "English",
    "fr-FR": "French",
};

// Joined courses with progress bars + the join-by-code form.
const MyCoursesCard = () => {
    const { t } = useTranslation();
    const [courses, setCourses] = useState(null);
    const [code, setCode] = useState("");
    const [joining, setJoining] = useState(false);
    const [joinError, setJoinError] = useState(null);
    const [joinSuccess, setJoinSuccess] = useState(null);

    const loadCourses = useCallback(() => {
        getMyCourses()
            .then(setCourses)
            .catch((error) => {
                console.error("Failed to load courses:", error);
                setCourses([]);
            });
    }, []);

    useEffect(() => {
        loadCourses();
    }, [loadCourses]);

    const handleJoin = (e) => {
        e.preventDefault();
        if (!code.trim() || joining) return;
        setJoining(true);
        setJoinError(null);
        setJoinSuccess(null);
        joinCourse(code.trim())
            .then((course) => {
                setCode("");
                setJoinSuccess(
                    course.already_member
                        ? t("courses.alreadyMember", { title: course.title })
                        : t("courses.joined", { title: course.title })
                );
                loadCourses();
            })
            .catch((error) => {
                setJoinError(courseErrorMessage(error, t));
            })
            .finally(() => setJoining(false));
    };

    return (
        <div className="rounded-lg border border-border bg-secondary p-4 md:p-6">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-medium">{t("courses.myCourses")}</h3>

                {/* Join with entry code */}
                <form onSubmit={handleJoin} className="flex items-center gap-2">
                    <input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder={t("courses.codePlaceholder")}
                        maxLength={6}
                        className="w-32 rounded-md border border-border px-3 py-1.5 text-sm uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                        type="submit"
                        disabled={joining || code.trim().length < 6}
                        className="rounded-md bg-primary px-3 py-1.5 text-sm text-white hover:bg-purple-600 disabled:opacity-50"
                    >
                        {t("courses.join")}
                    </button>
                </form>
            </div>

            {joinError && <p className="mt-2 text-sm text-red-600">{joinError}</p>}
            {joinSuccess && <p className="mt-2 text-sm text-green-700">{joinSuccess}</p>}

            {courses === null ? (
                <div className="mt-4 h-6 w-3/4 animate-pulse rounded bg-gray-200" />
            ) : courses.length === 0 ? (
                <p className="mt-3 text-sm text-gray-600">{t("courses.noCourses")}</p>
            ) : (
                <ul className="mt-4 flex flex-col gap-3">
                    {courses.map((course) => {
                        const { practiced_sentences, total_sentences, average_score } =
                            course.progress;
                        const percent = total_sentences
                            ? Math.round((practiced_sentences / total_sentences) * 100)
                            : 0;
                        return (
                            <li key={course.id}>
                                <Link
                                    to={`/courses/${course.id}`}
                                    className="block rounded-md border border-border bg-white p-3 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div className="min-w-0">
                                            <span className="font-medium">{course.title}</span>
                                            <span className="ml-2 text-xs text-gray-500">
                                                {t(LANGUAGE_LABEL_KEYS[course.language] || course.language)}
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            {average_score !== null && (
                                                <span className="mr-3">
                                                    Ø {average_score} {t("unit.points")}
                                                </span>
                                            )}
                                            <span>
                                                {practiced_sentences}/{total_sentences}{" "}
                                                {t("courses.sentences")}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                                        <div
                                            className="h-2 rounded-full bg-primary"
                                            style={{ width: `${percent}%` }}
                                        />
                                    </div>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default MyCoursesCard;
